import React, { memo } from "react";
import { Clock, CheckCircle, Eye, Settings, FileText } from "lucide-react";

// ChecklistItem Component
export const ChecklistItem = memo(
  ({ task, onOpenModal, onQuickComplete, isLoading, itemId }) => (
    <div
      className={`checklist-item compact ${task.completed ? "completed" : ""} ${
        task.isPlaceholder ? "placeholder" : ""
      } ${isLoading ? "loading" : ""}`}
    >
      <div className="checklist-checkbox">
        {isLoading ? (
          <div className="spinner-small" />
        ) : task.completed ? (
          <CheckCircle className="status-icon completed" size={14} />
        ) : (
          <Clock className="status-icon pending" size={14} />
        )}
      </div>
      <span className="task-text">
        {task.taskName}
        {task.isPlaceholder && (
          <span className="placeholder-indicator"> (Not started)</span>
        )}
      </span>
      {!task.completed && !isLoading && (
        <div className="checklist-actions">
          <button
            type="button"
            className="quick-complete-btn"
            onClick={() => onQuickComplete(task.id, itemId)}
            title="Complete without remarks"
          >
            <CheckCircle size={14} />
          </button>
          <button
            type="button"
            className="complete-with-remarks-btn"
            onClick={() => onOpenModal(task.id, task.isPlaceholder)}
            title="Complete with remarks"
          >
            <FileText size={14} />
          </button>
        </div>
      )}
    </div>
  )
);

ChecklistItem.displayName = "ChecklistItem";

// ChecklistSection Component
export const ChecklistSection = memo(
  ({
    checkListHistories,
    onOpenModal,
    onQuickComplete,
    isComplete,
    loadingItems,
    itemId,
  }) => (
    <div className="checklist-section compact">
      <div className="checklist-header">
        <span className="checklist-title">Tasks</span>
        <span
          className={`completion-badge ${
            isComplete ? "complete" : "incomplete"
          }`}
        >
          {checkListHistories.filter((task) => task.completed).length}/
          {checkListHistories.length}
        </span>
      </div>
      <div className="checklist-items">
        {checkListHistories.map((task) => (
          <ChecklistItem
            key={task.id}
            task={task}
            itemId={itemId}
            onOpenModal={onOpenModal}
            onQuickComplete={onQuickComplete}
            isLoading={loadingItems?.has(itemId) || false}
          />
        ))}
      </div>
    </div>
  )
);

ChecklistSection.displayName = "ChecklistSection";

// ProductsList Component
export const ProductsList = memo(
  ({ productHistories, onEdit, onProductClick, customPricing = false }) => {
    if (!productHistories || productHistories.length === 0) {
      return null;
    }

    return (
      <div className="products-list-section compact">
        <div className="products-header">
          <span className="products-title">Products</span>
          <span className="products-count">{productHistories.length}</span>
        </div>
        <div className="products-items">
          {productHistories.map((product) => (
            <div
              key={product.id}
              className="product-item-card clickable"
              onClick={() => onProductClick && onProductClick(product)}
              style={{ cursor: "pointer" }}
            >
              <div className="product-info">
                <span className="product-name">
                  {product.product?.name || "Unknown Product"}
                </span>
                <div className="product-details">
                  <span>Qty: {product.quantity}</span>
                  {product.price > 0 && (
                    <span
                      className={
                        customPricing ? "custom-price" : "standard-price"
                      }
                    >
                      Price: ₹{product.price.toFixed(2)}
                      {customPricing && (
                        <span className="price-badge">Custom</span>
                      )}
                    </span>
                  )}
                  {product.price > 0 && product.quantity && (
                    <span className="product-total">
                      Total: ₹{(product.price * product.quantity).toFixed(2)}
                    </span>
                  )}
                </div>

                {/* Show Specifications */}
                {product.specificationHistories &&
                  product.specificationHistories.length > 0 && (
                    <div className="product-specifications">
                      <span className="spec-label">Specifications:</span>
                      <div className="spec-list">
                        {product.specificationHistories.map((spec, idx) => (
                          <span key={idx} className="spec-badge">
                            <span className="spec-name">
                              {spec.productSpecification?.specName || "Spec"}
                            </span>
                            <span className="spec-separator">:</span>
                            <span className="spec-value">
                              {spec.productSpecificationValue?.value || "N/A"}
                              {spec.productSpecificationValue?.unit &&
                                ` ${spec.productSpecificationValue.unit}`}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
              <div className="product-actions">
                {onEdit && (
                  <button
                    className="edit-product-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(product, product.id);
                    }}
                    title="Edit product"
                  >
                    <Settings size={14} />
                  </button>
                )}
                <button
                  className="view-product-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onProductClick && onProductClick(product);
                  }}
                  title="View product details"
                >
                  <Eye size={14} />
                </button>
              </div>
            </div>
          ))}

          {/* Total Summary for Products */}
          {productHistories.length > 1 && (
            <div className="products-summary">
              <div className="summary-item">
                <span>Total Quantity:</span>
                <strong>
                  {productHistories.reduce(
                    (sum, p) => sum + (p.quantity || 0),
                    0
                  )}
                </strong>
              </div>
              {productHistories.every((p) => p.price > 0) && (
                <div className="summary-item total">
                  <span>Grand Total:</span>
                  <strong>
                    ₹
                    {productHistories
                      .reduce(
                        (sum, p) => sum + (p.price || 0) * (p.quantity || 0),
                        0
                      )
                      .toFixed(2)}
                  </strong>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

ProductsList.displayName = "ProductsList";
